import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';

import {
  getFileExtension,
  isImageSource,
  readImageDimensions,
  readImageDimensionsFromUrl,
  TImageDimensions,
} from '@shared/lib';
import { ToastService } from '@shared/ui';

import { language } from './language';
import { TSingleUploaderStatus, TUseSingleUploaderParams } from './types';

const DEFAULT_MAX_SIZE = 3;

const matchesDimensions = (
  current: TImageDimensions,
  required: TImageDimensions
) => current.width === required.width && current.height === required.height;

export const useSingleUploader = ({
  value,
  onChange,
  uploadFn,
  accept = 'image/*',
  maxSize = DEFAULT_MAX_SIZE,
  dimensions,
  dimensionErrorMessage,
  onDimensionsValidationChange,
  onError,
}: TUseSingleUploaderParams) => {
  const [status, setStatus] = useState<TSingleUploaderStatus>('idle');
  const [percent, setPercent] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [fileMeta, setFileMeta] = useState<{ name: string; size: number; type: string } | null>(
    null
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const localPreviewRef = useRef<string | null>(null);
  const uploadedImageDimensionsRef = useRef<TImageDimensions | null>(null);

  const setPreview = (url: string | null) => {
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current);
    }
    localPreviewRef.current = url;
    setLocalPreview(url);
  };

  useEffect(() => {
    return () => {
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current);
      }
    };
  }, []);

  const previewUrl = localPreview ?? value ?? null;
  const isImage = isImageSource(previewUrl, fileMeta?.type);
  const fileName = fileMeta?.name ?? (value ? decodeURIComponent(value.split('/').pop() ?? '') : '');
  const hasValue = Boolean(previewUrl);

  const getDimensionErrorMessage = () =>
    dimensionErrorMessage ??
    (dimensions
      ? language.dimensionError(dimensions.width, dimensions.height)
      : language.readDimensionsError);

  const setDimensionValidationError = (message: string) => {
    setErrorMessage(message);
    setStatus('error');
    onDimensionsValidationChange?.(false);
  };

  const setDimensionValidationSuccess = () => {
    setErrorMessage(null);
    setStatus('done');
    onDimensionsValidationChange?.(true);
  };

  useEffect(() => {
    if (!dimensions || !previewUrl) {
      if (!previewUrl) {
        uploadedImageDimensionsRef.current = null;
        onDimensionsValidationChange?.(true);
      }
      return;
    }

    if (status === 'uploading') return;

    let cancelled = false;

    const revalidateDimensions = async () => {
      let currentDimensions = uploadedImageDimensionsRef.current;

      if (!currentDimensions) {
        try {
          currentDimensions = await readImageDimensionsFromUrl(previewUrl);
          uploadedImageDimensionsRef.current = currentDimensions;
        } catch {
          if (!cancelled) {
            onDimensionsValidationChange?.(true);
          }
          return;
        }
      }

      if (cancelled) return;

      if (!matchesDimensions(currentDimensions, dimensions)) {
        setDimensionValidationError(getDimensionErrorMessage());
        return;
      }

      setDimensionValidationSuccess();
    };

    void revalidateDimensions();

    return () => {
      cancelled = true;
    };
  }, [dimensions, previewUrl, status]);

  const emitError = (message: string) => {
    setErrorMessage(message);
    if (onError) {
      onError(message);
    } else {
      ToastService.error(message);
    }
  };

  const acceptsFile = (file: File) => {
    if (!accept || accept === '*' || accept === '*/*') return true;

    const extension = `.${getFileExtension(file.name)}`;
    const rules = accept.split(',').map((rule) => rule.trim().toLowerCase());

    return rules.some((rule) => {
      if (!rule) return false;
      if (rule.endsWith('/*')) return file.type.startsWith(rule.slice(0, -1));
      if (rule.startsWith('.')) return rule === extension;
      return rule === file.type.toLowerCase();
    });
  };

  const validate = (file: File) => {
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      emitError(language.maxFileSizeError(maxSize));
      return false;
    }
    if (!acceptsFile(file)) {
      emitError(language.invalidFormatError);
      return false;
    }
    return true;
  };

  const validateDimensions = async (file: File) => {
    if (!dimensions || !file.type.startsWith('image/')) {
      uploadedImageDimensionsRef.current = null;
      return true;
    }

    try {
      const imageDimensions = await readImageDimensions(file);
      uploadedImageDimensionsRef.current = imageDimensions;

      if (!matchesDimensions(imageDimensions, dimensions)) {
        uploadedImageDimensionsRef.current = null;
        emitError(getDimensionErrorMessage());
        onDimensionsValidationChange?.(false);
        return false;
      }

      onDimensionsValidationChange?.(true);
      return true;
    } catch {
      uploadedImageDimensionsRef.current = null;
      emitError(language.readDimensionsError);
      onDimensionsValidationChange?.(false);
      return false;
    }
  };

  const uploadFile = async (file: File) => {
    setPreview(URL.createObjectURL(file));
    setFileMeta({ name: file.name, size: file.size, type: file.type });
    setErrorMessage(null);
    setPercent(0);
    setStatus('uploading');

    try {
      if (uploadFn) {
        const result = await uploadFn(file, ({ total, loaded }) => {
          if (total) {
            setPercent(Math.round((loaded * 100) / total));
          }
        });
        setPercent(100);
        setStatus('done');
        onChange?.(result);
        onDimensionsValidationChange?.(true);
      } else {
        setPercent(100);
        setStatus('done');
        onChange?.(localPreviewRef.current);
        onDimensionsValidationChange?.(true);
      }
    } catch (error) {
      setPreview(null);
      setFileMeta(null);
      uploadedImageDimensionsRef.current = null;
      setStatus('error');
      const message =
        error instanceof Error && error.message ? error.message : language.uploadFailedError;
      emitError(message);
      onDimensionsValidationChange?.(false);
    }
  };

  const handleSelectFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (!validate(file)) return;
    if (!(await validateDimensions(file))) return;
    void uploadFile(file);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSelectFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  const handleRemove = () => {
    setPreview(null);
    setFileMeta(null);
    uploadedImageDimensionsRef.current = null;
    setStatus('idle');
    setPercent(0);
    setErrorMessage(null);
    onChange?.(null);
    onDimensionsValidationChange?.(true);
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    handleSelectFile(event.dataTransfer.files?.[0]);
  };

  return {
    state: {
      status,
      percent,
      errorMessage,
      dragOver,
      previewUrl,
      isImage,
      fileName,
      hasValue,
      isUploading: status === 'uploading',
    },
    refs: { inputRef },
    handlers: {
      handleInputChange,
      handleSelectFile,
      openFileDialog,
      handleRemove,
      handleDragOver,
      handleDragLeave,
      handleDrop,
    },
  };
};
